/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useRef, useState } from "react";
import { EventContentArg, DatesSetArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { Button, theme } from "@nordcloud/gnui";
import { ROUTES } from "~/routing/routes";
import { DEFAULT_VIEW, VIEW_OPTIONS } from "../constants";
import { CalendarViewMode, TimelineEvent } from "../types";
import { EventPopover } from "./EventPopover";

type Props = {
  events: TimelineEvent[];
  onDateRangeChange: (start: Date, end: Date, view: string) => void;
  onFilterByPlan: (planId: string) => void;
};

export function TimelineCalendar({
  events,
  onDateRangeChange,
  onFilterByPlan,
}: Props) {
  const calendarRef = useRef<FullCalendar>(null);
  const history = useHistory();
  const [currentView, setCurrentView] =
    useState<CalendarViewMode>(DEFAULT_VIEW);
  const [popover, setPopover] = useState<{
    visible: boolean;
    x: number;
    y: number;
    event: TimelineEvent | null;
  }>({ visible: false, x: 0, y: 0, event: null });
  const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);
  const popoverHoveredRef = useRef(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDatesSet = useCallback(
    (dateInfo: DatesSetArg) => {
      setCurrentView(dateInfo.view.type as CalendarViewMode);
      onDateRangeChange(dateInfo.start, dateInfo.end, dateInfo.view.type);
    },
    [onDateRangeChange]
  );

  const handleEventClick = useCallback(() => {
    // Click is handled via popover action buttons
  }, []);

  const handleEventMouseEnter = useCallback(
    (info: {
      el: HTMLElement;
      event: {
        id: string;
        extendedProps: Record<string, unknown>;
        start: Date | null;
        end: Date | null;
      };
    }) => {
      // Cancel any pending hide
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      const rect = info.el.getBoundingClientRect();
      const matchedEvent = events.find((e) => e.id === info.event.id);
      if (matchedEvent) {
        setHoveredGroupId(matchedEvent.groupId);

        // Position popover: try right, then left, then center below
        const popoverWidth = 300;
        const viewportWidth = window.innerWidth;

        let x: number;
        if (rect.right + 8 + popoverWidth <= viewportWidth) {
          // Fits on the right
          x = rect.right + 8;
        } else if (rect.left - popoverWidth - 8 >= 0) {
          // Fits on the left
          x = rect.left - popoverWidth - 8;
        } else {
          // Neither side works — center it horizontally within viewport
          x = Math.max(8, (viewportWidth - popoverWidth) / 2);
        }

        // Clamp Y to stay within viewport
        const y = Math.min(Math.max(8, rect.top), window.innerHeight - 260);

        setPopover({
          visible: true,
          x,
          y,
          event: matchedEvent,
        });
      }
    },
    [events]
  );

  const handleEventMouseLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      if (!popoverHoveredRef.current) {
        setHoveredGroupId(null);
        setPopover({ visible: false, x: 0, y: 0, event: null });
      }
    }, 300);
  }, []);

  const handleViewChange = (view: CalendarViewMode) => {
    setCurrentView(view);
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.changeView(view);
    }
  };

  const handleToday = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.today();
    }
  };

  const handlePrev = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.prev();
    }
  };

  const handleNext = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.next();
    }
  };

  return (
    <CalendarWrapper>
      <ToolbarRow>
        <NavigationGroup>
          <Button
            severity="low"
            size="sm"
            onClick={handleToday}
            data-testid="timeline-nav-today"
          >
            Today
          </Button>
          <Button
            severity="low"
            size="sm"
            icon="chevronLeft"
            onClick={handlePrev}
            data-testid="timeline-nav-prev"
          />
          <Button
            severity="low"
            size="sm"
            icon="chevronRight"
            onClick={handleNext}
            data-testid="timeline-nav-next"
          />
        </NavigationGroup>
        <ViewSwitcher>
          {VIEW_OPTIONS.map((option) => (
            <Button
              key={option.value}
              severity={currentView === option.value ? "medium" : "low"}
              size="sm"
              onClick={() => handleViewChange(option.value)}
              data-testid={`timeline-view-${option.label.toLowerCase()}`}
            >
              {option.label}
            </Button>
          ))}
        </ViewSwitcher>
      </ToolbarRow>
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={DEFAULT_VIEW}
        events={events}
        headerToolbar={false}
        allDaySlot={false}
        datesSet={handleDatesSet}
        eventClick={handleEventClick}
        eventMouseEnter={handleEventMouseEnter}
        eventMouseLeave={handleEventMouseLeave}
        eventContent={renderEventContent}
        eventClassNames={(arg) => {
          const classes: string[] = [];
          const eventId = arg.event.id;
          if (eventId.startsWith("past-")) {
            classes.push("fc-event-past");
          }
          if (hoveredGroupId) {
            const groupId = arg.event.extendedProps.groupId as string;
            classes.push(
              groupId !== hoveredGroupId
                ? "fc-event-dimmed"
                : "fc-event-highlighted"
            );
          }
          return classes;
        }}
        height="auto"
        nowIndicator
        dayMaxEvents={5}
        eventDisplay="block"
        slotEventOverlap={false}
        eventOverlap={false}
        eventMinHeight={0}
      />
      {popover.visible && popover.event && (
        <PopoverPositioner
          style={{ left: popover.x, top: popover.y }}
          onMouseEnter={() => {
            popoverHoveredRef.current = true;
            if (hideTimeoutRef.current) {
              clearTimeout(hideTimeoutRef.current);
              hideTimeoutRef.current = null;
            }
          }}
          onMouseLeave={() => {
            popoverHoveredRef.current = false;
            setHoveredGroupId(null);
            setPopover({ visible: false, x: 0, y: 0, event: null });
          }}
        >
          <EventPopover
            planName={popover.event.planName}
            actionName={popover.event.actionName}
            scheduleTimezone={popover.event.scheduleTimezone}
            startTime={popover.event.start}
            endTime={popover.event.end}
            resourceCount={popover.event.resourceCount}
            inputParameters={popover.event.inputParameters}
            accentColor={popover.event.backgroundColor}
            isPast={popover.event.extendedProps.isPast}
            eventStatus={popover.event.extendedProps.eventStatus}
            actionStatus={popover.event.extendedProps.actionStatus}
            onGoToPlan={() => {
              history.push(
                ROUTES.plans.generateDetails(popover.event?.planId || "")
              );
              setPopover({ visible: false, x: 0, y: 0, event: null });
            }}
            onGoToEvent={() => {
              const eventId = popover.event?.groupId.replace("past-", "");
              history.push(ROUTES.events.generateDetails(eventId || ""));
              setPopover({ visible: false, x: 0, y: 0, event: null });
            }}
            onFilterPlan={() => {
              onFilterByPlan(popover.event?.planId || "");
              setPopover({ visible: false, x: 0, y: 0, event: null });
            }}
          />
        </PopoverPositioner>
      )}
    </CalendarWrapper>
  );
}

// eslint-disable-next-line complexity
function renderEventContent(eventInfo: EventContentArg) {
  const {
    planName,
    actionName,
    resourceCount,
    isFirstInGroup,
    isLastInGroup,
    isPast,
    actionStatus,
    prevActionEnd,
    nextActionStart,
  } = eventInfo.event.extendedProps as TimelineEvent["extendedProps"];
  const accentColor = eventInfo.event.backgroundColor;
  const borderColor = eventInfo.event.borderColor;
  const pastelBg = hexToPasstel(accentColor);
  const textColor = ensureReadableColor(accentColor);

  // Calculate duration in minutes to decide content density
  const start = eventInfo.event.start;
  const end = eventInfo.event.end;
  const durationMinutes =
    start && end ? (end.getTime() - start.getTime()) / (1000 * 60) : 60;

  const isShort = durationMinutes < 15;
  const statusEmoji =
    isPast && actionStatus ? getStatusEmoji(actionStatus) : null;

  // Determine if adjacent actions are "far away" (> 60 min gap)
  const GAP_THRESHOLD_MS = 60 * 60 * 1000; // 60 minutes
  const hasDistantPrev =
    !isFirstInGroup && prevActionEnd && start
      ? start.getTime() - new Date(prevActionEnd).getTime() > GAP_THRESHOLD_MS
      : false;
  const hasDistantNext =
    !isLastInGroup && nextActionStart && end
      ? new Date(nextActionStart).getTime() - end.getTime() > GAP_THRESHOLD_MS
      : false;

  // Show continuity line if not the only action in the group
  const showContinuityLine = !isFirstInGroup || !isLastInGroup;

  return (
    <EventContentWrapper data-testid={`timeline-event-${eventInfo.event.id}`}>
      {showContinuityLine && <ContinuityLine />}
      {isFirstInGroup && <GroupMarkerAbove>▶ Start plan</GroupMarkerAbove>}
      {!isFirstInGroup && hasDistantPrev && (
        <ContinuationMarker $position="above">
          ⋯ more actions before
        </ContinuationMarker>
      )}
      <EventBlock
        $bgColor={pastelBg}
        $accentColor={accentColor}
        $borderColor={borderColor}
      >
        <EventActionName $textColor={textColor}>
          {statusEmoji && <StatusIcon>{statusEmoji}</StatusIcon>}
          {actionName}
        </EventActionName>
        {!isShort && (
          <>
            <EventPlanLabel>{planName}</EventPlanLabel>
            {resourceCount > 0 && (
              <ResourceBadge $accentColor={textColor}>
                {resourceCount} resource{resourceCount !== 1 ? "s" : ""}
              </ResourceBadge>
            )}
          </>
        )}
        {isShort && resourceCount > 0 && (
          <ResourceBadge $accentColor={textColor}>
            {resourceCount}
          </ResourceBadge>
        )}
      </EventBlock>
      {!isLastInGroup && hasDistantNext && (
        <ContinuationMarker $position="below">
          ⋯ more actions after
        </ContinuationMarker>
      )}
      {isLastInGroup && <GroupMarkerBelow>◼ End plan</GroupMarkerBelow>}
    </EventContentWrapper>
  );
}

function getStatusEmoji(status: string): string {
  switch (status) {
    case "SUCCESS":
      return "✅";
    case "FAILED":
      return "❌";
    case "PARTIAL_SUCCESS":
      return "⚠️";
    case "IN_PROGRESS":
      return "🔄";
    case "TIMEOUT":
      return "⏱️";
    case "APPROVAL_REQUIRED":
    case "APPROVAL_TIMEOUT":
      return "🔒";
    default:
      return "⏳";
  }
}

/**
 * Converts a hex color to a pastel version by mixing with white.
 * Result is ~85% white + 15% original color for a soft background.
 */
function hexToPasstel(hex: string): string {
  const clean = hex.replace("#", "");
  if (clean.length < 6) {
    return "#f5f5f5";
  }

  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);

  // Mix with white (85% white, 15% color)
  const pr = Math.round(r * 0.15 + 255 * 0.85);
  const pg = Math.round(g * 0.15 + 255 * 0.85);
  const pb = Math.round(b * 0.15 + 255 * 0.85);

  return `#${pr.toString(16).padStart(2, "0")}${pg
    .toString(16)
    .padStart(2, "0")}${pb.toString(16).padStart(2, "0")}`;
}

/**
 * Ensures a color has enough contrast to be readable as text on a white/pastel background.
 * If the color is too light (e.g., bright yellow), darkens it.
 */
function ensureReadableColor(hex: string): string {
  const clean = hex.replace("#", "");
  if (clean.length < 6) {
    return "#333333";
  }

  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;

  // Calculate relative luminance
  const rLin = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const gLin = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const bLin = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
  const luminance = 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;

  // If too light for text, darken by 40%
  if (luminance > 0.4) {
    const dr = Math.round(r * 255 * 0.6);
    const dg = Math.round(g * 255 * 0.6);
    const db = Math.round(b * 255 * 0.6);
    return `#${dr.toString(16).padStart(2, "0")}${dg
      .toString(16)
      .padStart(2, "0")}${db.toString(16).padStart(2, "0")}`;
  }

  return hex;
}

const CalendarWrapper = styled.div`
  position: relative;

  .fc {
    font-family: inherit;
  }

  .fc-timegrid-slot {
    height: 3em;
  }

  .fc-event {
    cursor: pointer;
    border: none;
    border-radius: 6px;
    padding: 0;
    margin-bottom: 2px;
    background: transparent !important;
  }

  .fc-timegrid-event {
    border-radius: 6px;
    overflow: visible;
    background: transparent !important;
    border: none !important;
  }

  .fc-timegrid-event-harness {
    overflow: visible;
  }

  .fc-timegrid-event-harness:hover {
    z-index: 100 !important;
  }

  .fc-timegrid-event:hover {
    z-index: 100 !important;
    min-height: 38px;
    box-shadow: ${theme.shadow.shadow04};
    overflow: visible;
  }

  .fc-event-dimmed {
    opacity: 0.35;
    transition: opacity 0.2s ease;
  }

  .fc-event-highlighted {
    opacity: 1;
    transition: opacity 0.2s ease;
  }

  .fc-event-past {
    opacity: 0.55;
    transition: opacity 0.2s ease;
  }

  .fc-event-past:hover,
  .fc-event-past.fc-event-highlighted {
    opacity: 1;
  }

  .fc-event-past.fc-event-dimmed {
    opacity: 0.2;
  }

  /* Ensure markers inside highlighted events are fully visible regardless of parent opacity */
  .fc-event.fc-event-highlighted {
    opacity: 1 !important;
  }

  .fc-day-today {
    background-color: #f7f7f8 !important;
  }

  .fc-timegrid-col.fc-day-today {
    box-shadow: inset 0 0 0 1px #e0e0e4;
  }

  .fc-col-header-cell {
    padding: ${theme.spacing.spacing02};
    font-weight: 500;
  }

  .fc-col-header-cell.fc-day-today {
    font-weight: 700;
    color: ${theme.color.interactive.primary};
    background-color: ${theme.color.interactive.primary}14;
    position: relative;
  }

  .fc-col-header-cell.fc-day-today::after {
    content: "";
    display: block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${theme.color.interactive.primary};
    margin: 2px auto 0;
  }

  .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
    background-color: ${theme.color.interactive.primary};
    color: #ffffff;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.spacing04};
`;

const NavigationGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.spacing02};
`;

const ViewSwitcher = styled.div`
  display: flex;
  gap: ${theme.spacing.spacing01};
`;

const PopoverPositioner = styled.div`
  position: fixed;
  z-index: 1000;
  pointer-events: auto;
`;

const EventBlock = styled.div<{
  $bgColor: string;
  $accentColor: string;
  $borderColor: string;
}>`
  background-color: ${({ $bgColor }) => $bgColor};
  border: 1.5px solid ${({ $accentColor }) => $accentColor};
  border-left: 4px solid ${({ $borderColor }) => $borderColor};
  border-radius: 6px;
  padding: 6px 10px;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
`;

const EventActionName = styled.div<{ $textColor: string }>`
  font-size: 12px;
  font-weight: 600;
  color: ${({ $textColor }) => $textColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusIcon = styled.span`
  font-size: 11px;
  flex-shrink: 0;
`;

const ResourceBadge = styled.span<{ $accentColor: string }>`
  font-size: 9px;
  font-weight: 500;
  color: ${({ $accentColor }) => $accentColor};
  background: transparent;
  border: 1px solid ${({ $accentColor }) => $accentColor}40;
  border-radius: 10px;
  padding: 0 5px;
  white-space: nowrap;
  width: fit-content;
  line-height: 1.4;
`;

const EventPlanLabel = styled.div`
  font-size: 10px;
  color: ${theme.color.text.text03};
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

/** Magenta accent for plan group markers */
const MARKER_COLOR = "#c2185b";

const EventContentWrapper = styled.div`
  position: relative;
  height: 100%;
`;

const ContinuityLine = styled.div`
  display: none;
`;

const GroupMarkerAbove = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${MARKER_COLOR};
  opacity: 0;
  line-height: 1;
  transition: opacity 0.2s ease;
  position: absolute;
  top: -16px;
  left: 0;
  white-space: nowrap;
  pointer-events: none;
  text-shadow: 0 0 6px #fff, 0 0 12px #fff, 0 0 18px #fff, 0 0 24px #fff;

  .fc-event-highlighted & {
    opacity: 1;
  }
`;

const GroupMarkerBelow = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${MARKER_COLOR};
  opacity: 0;
  line-height: 1;
  transition: opacity 0.2s ease;
  position: absolute;
  bottom: -16px;
  left: 0;
  white-space: nowrap;
  pointer-events: none;
  text-shadow: 0 0 6px #fff, 0 0 12px #fff, 0 0 18px #fff, 0 0 24px #fff;

  .fc-event-highlighted & {
    opacity: 1;
  }
`;

const ContinuationMarker = styled.div<{ $position: "above" | "below" }>`
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: ${MARKER_COLOR};
  opacity: 0;
  line-height: 1;
  transition: opacity 0.2s ease;
  position: absolute;
  ${({ $position }) =>
    $position === "above" ? "top: -14px;" : "bottom: -14px;"}
  left: 0;
  white-space: nowrap;
  pointer-events: none;
  text-shadow: 0 0 6px #fff, 0 0 12px #fff, 0 0 18px #fff, 0 0 24px #fff;

  .fc-event-highlighted & {
    opacity: 1;
  }
`;
